<template>
	<div class="flex flex-col" style="width: 100%; height: calc(100vh - 180px); min-height: 450px;">
		<div v-if="!isLoading" class="flex-grow overflow-y-auto pr-4">
			<div class="text-left pb-6">
				<div class="text-lg mb-4 font-bold">
					{{ $t('accident_data') }}
				</div>

				<DamageAssessorCaseAccidentDataForm ref="AccidentDataForm" v-model="accident" />

				<div class="text-lg mt-8 mb-4 font-bold">
					{{ $t('opponent_data') }}
				</div>

				<DamageAssessorCaseOppositePersonForm ref="OppositePersonForm" v-model="oppositeParty" />
			</div>
		</div>

		<div v-else class="flex-grow flex items-center justify-center">
			<LoadingSpinner />
		</div>

		<div v-if="!isLoading" class="flex justify-end flex-shrink-0 pt-4 mt-4 border-t bg-white">
			<BaseButton @click="close" class="mr-2" :disabled="isSaving">
				{{ $t('general.cancel') }}
			</BaseButton>

			<SaveButton @click="save" :disabled="isSaving" class="btn-blue">
				{{ $t('general.save') }}
			</SaveButton>
		</div>
	</div>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

import DamageAssessorCaseAccidentDataForm from '@/components/damageassessorcases/forms/DamageAssessorCaseAccidentDataForm.vue';
import DamageAssessorCaseOppositePersonForm from '@/components/damageassessorcases/forms/DamageAssessorCaseOppositePersonForm.vue';

import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';

export default {
	name: 'DamageAssessorCaseEditAccidentModal',

	components: {
		BaseButton,
		SaveButton,
		LoadingSpinner,

		DamageAssessorCaseAccidentDataForm,
		DamageAssessorCaseOppositePersonForm,
	},

	data() {
		return {
			isLoading: true,
			isSaving: false,

			damageAssessorCase: null,
			accident: null,
			oppositeParty: null,
			initialState: null,
		};
	},

	computed: {
		hasChanges() {
			if (!this.initialState || !this.accident || !this.oppositeParty) {
				return false;
			}
			return JSON.stringify(this.createComparableState()) !== JSON.stringify(this.initialState);
		},
	},

	watch: {
		hasChanges(value) {
			this.$store.commit('setConfirmLeaving', value ? this.$t('leave_page') : false);
		},
	},

	mounted() {
		this.load();
	},

	destroyed() {
		this.$store.commit('setConfirmLeaving', false);
	},

	methods: {
		load() {
			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.getDamageAssessorCaseDetail(this.$route.params.id)
				.then(response => {
					this.damageAssessorCase = response.data;
					this.loadAccidentData();
					this.loadOppositePartyData();
					this.initialState = this.createComparableState();
				})
				.catch(() => {
					this.showError();
				})
				.finally(() => {
					this.isLoading = false;
				});
		},

		loadAccidentData() {
			this.accident = {
				date: this.damageAssessorCase?.date_of_accident ?? null,
				damageTimeSetByUser: this.damageAssessorCase?.damage_time_set_by_user ?? false,
				accidentLocation: this.damageAssessorCase?.accident_location ?? null,
				isRecordedByPolice: this.damageAssessorCase?.is_recorded_by_police ?? null,
				isPersonInjured: this.damageAssessorCase?.is_person_injured ?? null,
				accidentReport: this.findAccidentReport(),
			};
		},

		loadOppositePartyData() {
			const oppositePerson = this.damageAssessorCase?.opposite_person ?? null;

			this.oppositeParty = {
				oppositePerson: oppositePerson
					? {
							firstName: oppositePerson.firstName ?? oppositePerson.first_name ?? null,
							lastName: oppositePerson.lastName ?? oppositePerson.last_name ?? null,
					  }
					: null,

				oppositeLicensePlate: {
					text: this.damageAssessorCase?.causer_license_plate ?? null,
					isSpecial: this.normalizeBoolean(this.damageAssessorCase?.is_causer_license_plate_special),
				},

				insuranceName: this.damageAssessorCase?.opposite_insurance_name ?? null,
				insuranceNumber: this.damageAssessorCase?.opposite_insurance_number ?? null,
				insuranceCaseNumber: this.damageAssessorCase?.opposite_insurance_case_number ?? null,
			};
		},
		normalizeBoolean(value) {
			return value === true || value === 1 || value === '1';
		},
		findDamageAssessorAccidentReport() {
			const media = this.damageAssessorCase?.media ?? [];
			return media.find(item => item.category === 'accident_report') ?? null;
		},
		findBrokerAccidentReport() {
			const media = this.damageAssessorCase?.broker_case?.media ?? [];
			return media.find(item => item.category === 'accident_report') ?? null;
		},
		findAccidentReport() {
			return this.findDamageAssessorAccidentReport() ?? this.findBrokerAccidentReport() ?? null;
		},
		hasOppositePerson() {
			const person = this.oppositeParty?.oppositePerson;
			return !!(person && (person.firstName || person.lastName));
		},
		buildDto() {
			const dto = {
				damage_assessor_case: this.buildDamageAssessorCaseDto(),
			};

			dto.opposite_person = this.hasOppositePerson() ? this.buildOppositePersonDto() : null;
			const accidentReport = this.buildAccidentReportDto();

			if (accidentReport) {
				dto.accident_report = accidentReport;
			}

			return dto;
		},
		buildDamageAssessorCaseDto() {
			return {
				date_of_accident: this.accident?.date ?? null,
				damage_time_set_by_user: this.accident?.damageTimeSetByUser ?? false,
				accident_location: this.accident?.accidentLocation ?? null,
				is_recorded_by_police: this.accident?.isRecordedByPolice ?? null,
				is_person_injured: this.accident?.isPersonInjured ?? null,
				causer_license_plate: this.oppositeParty?.oppositeLicensePlate?.text ?? null,
				is_causer_license_plate_special: this.oppositeParty?.oppositeLicensePlate?.isSpecial ?? false,
				opposite_insurance_name: this.oppositeParty?.insuranceName ?? null,
				opposite_insurance_number: this.oppositeParty?.insuranceNumber ?? null,
				opposite_insurance_case_number: this.oppositeParty?.insuranceCaseNumber ?? null,
			};
		},
		buildOppositePersonDto() {
			const person = this.oppositeParty?.oppositePerson;
			if (!person) {
				return null;
			}
			return {
				firstName: person.firstName ?? null,

				lastName: person.lastName ?? null,
			};
		},
		buildAccidentReportDto() {
			const report = this.accident?.accidentReport ?? null;
			if (!report) {
				return null;
			}
			if (report.id) {
				return null;
			}
			return report;
		},
		save() {
			if (this.isSaving || !this.$refs.AccidentDataForm || !this.$refs.OppositePersonForm) {
				return;
			}
			const isAccidentValid = this.$refs.AccidentDataForm.validate();
			const isOpponentValid = this.$refs.OppositePersonForm.validate();
			if (!isAccidentValid || !isOpponentValid) {
				this.showValidationError();
				return;
			}

			this.isSaving = true;

			const dto = this.buildDto();

			new DamageAssessorCaseRepository()
				.updateDamageAssessorCase(this.damageAssessorCase.id, dto)
				.then(() => {
					this.$root.$emit('updateCase');
					this.$store.commit('setConfirmLeaving', false);
					this.$router.go(-1);
					return this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
				})
				.catch(() => {
					this.showError();
				})
				.finally(() => {
					this.isSaving = false;
				});
		},
		createComparableState() {
			return {
				accident: {
					date: this.accident?.date ?? null,
					damageTimeSetByUser: this.accident?.damageTimeSetByUser ?? false,
					accidentLocation: this.accident?.accidentLocation ?? null,
					isRecordedByPolice: this.accident?.isRecordedByPolice ?? null,
					isPersonInjured: this.accident?.isPersonInjured ?? null,
					accidentReport: this.accident?.accidentReport
						? {
								id: this.accident.accidentReport.id ?? null,
								base64content: this.accident.accidentReport.base64content ?? null,
						  }
						: null,
				},

				oppositeParty: {
					oppositePerson: {
						firstName: this.oppositeParty?.oppositePerson?.firstName ?? null,
						lastName: this.oppositeParty?.oppositePerson?.lastName ?? null,
					},
					oppositeLicensePlate: {
						text: this.oppositeParty?.oppositeLicensePlate?.text ?? null,
						isSpecial: this.oppositeParty?.oppositeLicensePlate?.isSpecial === true,
					},
					insuranceName: this.oppositeParty?.insuranceName ?? null,
					insuranceNumber: this.oppositeParty?.insuranceNumber ?? null,
					insuranceCaseNumber: this.oppositeParty?.insuranceCaseNumber ?? null,
				},
			};
		},

		close() {
			this.$store.commit('setConfirmLeaving', false);
			this.$router.go(-1);
		},
		showValidationError() {
			this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
		},
		showError() {
			this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
		},
	},
};
</script>

<style scoped></style>
